import type { PagingActions, PagingOptions } from "../../models/types"
import {
  getAllRegions,
  getRegion,
  getRegionsByChapterAndLicenceIds,
  getAllRegionsByChapterId as getRegionsByChapterId,
  postRegion,
  putRegion,
  removeRegion
} from "../../services/RegionService"
import { getQueryString } from "../../utils"
import { history } from "../Store"
import type { AppState } from "../app.state"
import type { Chapter } from "../chapters/chapter.types"
import type { PaginatedListOfRegionDto, RegionDetailDto, RegionDto } from "./../../models/models"
import type { AppThunkAction, AppThunkDispatch } from "./../app.types"
import type { Region, RegionAction, RegionListAction } from "./region.types"

export const REGION_CREATE: string = "REGION_CREATE"

export const REGION_FETCH_LIST: string = "REGION_FETCH_LIST"
export const REGION_FETCH: string = "REGION_FETCH"

export const REGION_UPDATE: string = "REGION_UPDATE"

export const REGION_DELETE: string = "REGION_DELETE"

export const REGION_LOAD_DEFAULT: string = "REGION_LOAD_DEFAULT"
export const REGION_LOAD_LIST: string = "REGION_LOAD_LIST"
export const REGION_LOAD: string = "REGION_LOAD"

export const REGION_CHANGE_PAGE: string = "REGION_CHANGE_PAGE"
export const REGION_LOAD_LIST_OPTIONS: string = "REGION_LOAD_LIST_OPTIONS"

export function createRegion(region: Region): AppThunkAction<void, RegionAction> {
  return async (dispatch: AppThunkDispatch<RegionAction>) => {
    dispatch({ type: REGION_CREATE })
    const newRegionId: string = await postRegion(region)
    const newRegion: RegionDetailDto = await getRegion(newRegionId)

    history.replace(`/manage/regions/${newRegionId}`)
    dispatch(loadRegion(newRegion))
  }
}

export function fetchRegionListByChapterAndLicenceIds(
  chapterId: string,
  licenceIds: string[]
): AppThunkAction<void, RegionListAction> {
  return async (dispatch: AppThunkDispatch<RegionListAction>) => {
    dispatch({ type: REGION_FETCH_LIST })

    const regions: RegionDto[] = await getRegionsByChapterAndLicenceIds(chapterId, licenceIds)

    dispatch(loadRegionList(regions))
  }
}

export function fetchAllRegions(pageIndex = 0, pageSize = 50): AppThunkAction<void, RegionListAction | PagingActions> {
  return async (dispatch: AppThunkDispatch<RegionListAction | PagingActions>, getState: () => AppState) => {
    dispatch({ type: REGION_FETCH_LIST })

    const state: AppState = getState()
    const queryString: string = getQueryString(pageIndex, pageSize, state.contentManager.sort)

    const pagedList: PaginatedListOfRegionDto = await getAllRegions(queryString)
    const pagingOptions: PagingOptions = {
      currentPage: pagedList.pageIndex,
      totalPages: pagedList.totalPages,
      pageSize: pageSize
    }
    dispatch(loadRegionList(pagedList.items as RegionDto[]))
    dispatch({ type: REGION_LOAD_LIST_OPTIONS, payload: pagingOptions })
  }
}

export function fetchRegionsByChapterId(chapterId: string): AppThunkAction<void, RegionListAction> {
  return async (dispatch: AppThunkDispatch<RegionListAction>) => {
    dispatch({ type: REGION_FETCH_LIST })

    const regions: RegionDto[] = await getRegionsByChapterId(chapterId)

    dispatch(loadRegionList(regions))
  }
}

export function fetchRegion(id: string): AppThunkAction<void, RegionAction> {
  return async (dispatch: AppThunkDispatch<RegionAction>) => {
    dispatch({ type: REGION_FETCH })

    const region: RegionDetailDto = await getRegion(id)

    dispatch(loadRegion(region))
  }
}

export function updateRegion(region: Region): AppThunkAction<void, RegionAction> {
  return async (dispatch: AppThunkDispatch<RegionAction>) => {
    dispatch({ type: REGION_UPDATE })
    await putRegion(region)
    const updatedRegion: RegionDetailDto = await getRegion(region.id as string)
    dispatch(loadRegion(updatedRegion))
  }
}

export function deleteRegion(id: string): AppThunkAction<void, RegionAction> {
  return async (dispatch: AppThunkDispatch<RegionAction>) => {
    dispatch({ type: REGION_DELETE })
    await removeRegion(id)
    dispatch(fetchAllRegions())
  }
}

export function changeRegionPage(page: number): AppThunkAction<void, PagingActions> {
  return (dispatch: AppThunkDispatch<PagingActions>, getState: () => AppState) => {
    const state = getState()
    dispatch({ type: REGION_CHANGE_PAGE, payload: page })

    dispatch(fetchAllRegions(page, state.region.pagingOptions.pageSize))
  }
}

export function loadDefaultRegion(): RegionAction {
  return {
    type: REGION_LOAD_DEFAULT
  }
}

export function loadRegionList(dtoList: RegionDto[]): RegionListAction {
  const regionList: Region[] = dtoList.map(regionDtoToRegion)
  return {
    type: REGION_LOAD_LIST,
    payload: regionList
  }
}

export function loadRegion(dto: RegionDetailDto): RegionAction {
  const region: Region = regionDetailDtoToRegion(dto)
  return {
    type: REGION_LOAD,
    payload: region
  }
}

function regionDetailDtoToRegion(dto: RegionDetailDto): Region {
  const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id)
  return {
    ...dto,
    id: dto.id,
    chapterId: dto.chapterId,
    chapter: dto.chapter as Chapter,
    name: dto.name as string,
    licenceIds: licenceIds ? licenceIds : []
  }
}

function regionDtoToRegion(dto: RegionDto): Region {
  const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id)
  return {
    ...dto,
    id: dto.id,
    name: dto.name as string,
    chapter: dto.chapter as Chapter,
    licenceIds: licenceIds ? licenceIds : []
  }
}
